import React, { createContext, useContext, useState, ReactNode } from "react";
import {
  Creative,
  CreativeContextType,
  CreateCreativeInput,
} from "../types/creative.types";

const CreativeContext = createContext<CreativeContextType | undefined>(
  undefined
);

export const useCreatives = () => {
  const context = useContext(CreativeContext);
  if (!context) {
    throw new Error("useCreatives must be used within a CreativeProvider");
  }
  return context;
};

interface CreativeProviderProps {
  children: ReactNode;
}

export const CreativeProvider: React.FC<CreativeProviderProps> = ({
  children,
}) => {
  // Initialize with empty array for dynamic data only
  const [creatives, setCreatives] = useState<Creative[]>([]);

  // Computed properties to filter creatives by their status
  const regularCreatives = creatives.filter(
    (creative) => !creative.isScheduled
  );
  const scheduledCreatives = creatives.filter(
    (creative) => creative.isScheduled
  );

  // Add regular creative
  const addCreative = (creative: CreateCreativeInput) => {
    const newCreative: Creative = {
      ...creative,
      id: Date.now().toString(),
      createdOn: new Date().toLocaleDateString(),
      isPlaying: false,
      dateRange: creative.dateRange || "N/A",
      locations: creative.locations || "N/A",
      tags: creative.tags || [],
      format: creative.orientation === "portrait" ? "Portrait" : "Landscape",
      fileFormat: creative.fileFormat,
      isScheduled: false,
      duration: creative.duration || 10, // Default duration of 10 seconds
    };
    setCreatives((prev) => [...prev, newCreative]);
    return newCreative;
  };

  // Add scheduled creative
  const addScheduledCreative = (creative: CreateCreativeInput) => {
    const newCreative: Creative = {
      ...creative,
      id: `scheduled_${Date.now().toString()}`,
      createdOn: new Date().toLocaleDateString(),
      isPlaying: false,
      dateRange: creative.dateRange || "Not scheduled",
      locations: creative.locations || "No locations",
      tags: creative.tags || [],
      format: creative.orientation === "portrait" ? "Portrait" : "Landscape",
      fileFormat: creative.fileFormat,
      isScheduled: true,
      duration: creative.duration || 10, // Default duration of 10 seconds
    };
    setCreatives((prev) => [...prev, newCreative]);
    return newCreative;
  };

  // Update creative by ID (works for both scheduled and regular)
  const updateCreative = (id: string, updates: Partial<Creative>) => {
    setCreatives((prev) =>
      prev.map((creative) =>
        creative.id === id ? { ...creative, ...updates } : creative
      )
    );
  };

  // Delete creative by ID (works for both scheduled and regular)
  const deleteCreative = (id: string) => {
    setCreatives((prev) => prev.filter((creative) => creative.id !== id));
  };

  // Global update (same as updateCreative now since we have single array)
  const updateCreativeGlobally = (id: string, updates: Partial<Creative>) => {
    setCreatives((prev) =>
      prev.map((creative) =>
        creative.id === id ? { ...creative, ...updates } : creative
      )
    );
  };

  // Global delete (same as deleteCreative now since we have single array)
  const deleteCreativeGlobally = (id: string) => {
    setCreatives((prev) => prev.filter((creative) => creative.id !== id));
  };

  // Move creative from regular to scheduled (change isScheduled flag)
  const moveToScheduled = (id: string, scheduleData: any) => {
    setCreatives((prev) =>
      prev.map((creative) =>
        creative.id === id
          ? {
              ...creative,
              isScheduled: true,
              scheduleDetails: scheduleData,
            }
          : creative
      )
    );
  };

  // Move creative from scheduled to regular (change isScheduled flag)
  const moveToRegular = (id: string) => {
    setCreatives((prev) =>
      prev.map((creative) =>
        creative.id === id
          ? {
              ...creative,
              isScheduled: false,
              scheduleDetails: undefined,
            }
          : creative
      )
    );
  };

  return (
    <CreativeContext.Provider
      value={{
        // Return computed arrays instead of separate state
        creatives: regularCreatives,
        scheduledCreatives: scheduledCreatives,
        addCreative,
        addScheduledCreative,
        updateCreative,
        deleteCreative,
        updateCreativeGlobally,
        deleteCreativeGlobally,
        moveToScheduled,
        moveToRegular,
        // Also provide access to all creatives if needed
        allCreatives: creatives,
      }}
    >
      {children}
    </CreativeContext.Provider>
  );
};